(() => {
  if (customElements.get('quiz-photo-cropper')) return;

  class QuizPhotoCropper extends HTMLElement {
    constructor() {
      super();

      this.image = null;
      this.originalFile = null;
      this.croppedFile = null;
      this.imageUrl = null;
      this.resultUrl = null;
      this.baseScale = 1;
      this.scale = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.drag = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.outputSize = Number(this.dataset.outputSize || 1500);
      this.maxFileSize = Number(this.dataset.maxFileSize || 20 * 1024 * 1024);

      this.sourceInput = this.querySelector('.qpc-source-input');
      this.dialog = this.querySelector('.qpc-dialog');
      this.canvas = this.querySelector('.qpc-canvas');
      this.context = this.canvas.getContext('2d', { alpha: true });
      this.zoomInput = this.querySelector('.qpc-zoom');
      this.applyButton = this.querySelector('.qpc-apply-button');
      this.chooseAgainButton = this.querySelector('.qpc-choose-again');
      this.closeButton = this.querySelector('.qpc-close-button');
      this.editButton = this.querySelector('.qpc-edit-button');
      this.result = this.querySelector('.qpc-result');
      this.resultImage = this.querySelector('.qpc-result-image');
      this.error = this.querySelector('.qpc-error');

      if (!this.form || !this.sourceInput || !this.dialog || !this.canvas) {
        this.showError('Billedfeltet kunne ikke forbindes til produktformularen. Kontakt os gerne.');
        return;
      }

      this.bindEvents();
      this.hideDynamicCheckout();
    }

    bindEvents() {
      this.sourceInput.addEventListener('change', () => this.handleFileSelection());
      this.zoomInput.addEventListener('input', () => this.handleZoom());
      this.applyButton.addEventListener('click', () => this.applyCrop());
      this.chooseAgainButton.addEventListener('click', () => this.chooseAnotherPhoto());
      this.closeButton.addEventListener('click', () => this.cancelCrop());
      this.editButton.addEventListener('click', () => this.openDialog());

      this.canvas.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.canvas.addEventListener('pointermove', (event) => this.moveDrag(event));
      this.canvas.addEventListener('pointerup', (event) => this.endDrag(event));
      this.canvas.addEventListener('pointercancel', (event) => this.endDrag(event));
      this.canvas.addEventListener('keydown', (event) => this.handleKeyboardMove(event));

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.cancelCrop();
      });

      this.form.addEventListener('submit', (event) => this.validateBeforeSubmit(event), true);
      this.form.addEventListener('formdata', (event) => this.appendFilesToFormData(event));
      this.form.addEventListener('reset', () => this.reset());
    }

    hideDynamicCheckout() {
      if (this.dataset.hideDynamicCheckout === 'false') return;

      const scope = this.closest('[id^="ProductInfo-"]') || this.parentElement || document;
      const hide = () => {
        scope.querySelectorAll('.shopify-payment-button').forEach((element) => {
          element.hidden = true;
          element.setAttribute('aria-hidden', 'true');
        });
      };

      hide();
      this.checkoutObserver = new MutationObserver(hide);
      this.checkoutObserver.observe(scope, { childList: true, subtree: true });
      window.setTimeout(() => this.checkoutObserver?.disconnect(), 10000);
    }

    async handleFileSelection() {
      const file = this.sourceInput.files?.[0];
      this.clearError();
      this.clearCropResult();

      if (!file) return;

      if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
        this.rejectFile('Vælg en billedfil i JPG, PNG, WEBP eller HEIC-format.');
        return;
      }

      if (file.size > this.maxFileSize) {
        this.rejectFile('Billedet er for stort. Vælg et billede på højst 20 MB.');
        return;
      }

      this.originalFile = file;

      try {
        await this.loadImage(file);
        this.initializeTransform();
        this.openDialog();
      } catch (error) {
        console.error('Quiz photo cropper could not decode image', error);
        this.rejectFile(
          'Din browser kunne ikke åbne billedet. Prøv at gemme det som JPG eller PNG og vælg det igen.'
        );
      }
    }

    loadImage(file) {
      return new Promise((resolve, reject) => {
        this.revokeImageUrl();
        this.imageUrl = URL.createObjectURL(file);

        const image = new Image();
        image.onload = () => {
          this.image = image;
          resolve();
        };
        image.onerror = reject;
        image.src = this.imageUrl;
      });
    }

    initializeTransform() {
      const size = this.canvas.width;
      this.baseScale = Math.max(size / this.image.naturalWidth, size / this.image.naturalHeight);
      this.scale = this.baseScale;
      this.offsetX = (size - this.image.naturalWidth * this.scale) / 2;
      this.offsetY = (size - this.image.naturalHeight * this.scale) / 2;
      this.zoomInput.value = '1';
      this.constrainOffsets();
      this.render();
    }

    handleZoom() {
      if (!this.image) return;

      const size = this.canvas.width;
      const oldScale = this.scale;
      const centerSourceX = (size / 2 - this.offsetX) / oldScale;
      const centerSourceY = (size / 2 - this.offsetY) / oldScale;

      this.scale = this.baseScale * Number(this.zoomInput.value);
      this.offsetX = size / 2 - centerSourceX * this.scale;
      this.offsetY = size / 2 - centerSourceY * this.scale;

      this.constrainOffsets();
      this.render();
    }

    startDrag(event) {
      if (!this.image) return;

      this.canvas.setPointerCapture(event.pointerId);
      this.drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      this.canvas.classList.add('is-dragging');
    }

    moveDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const rect = this.canvas.getBoundingClientRect();
      const factor = this.canvas.width / rect.width;
      this.offsetX += (event.clientX - this.drag.x) * factor;
      this.offsetY += (event.clientY - this.drag.y) * factor;
      this.drag.x = event.clientX;
      this.drag.y = event.clientY;

      this.constrainOffsets();
      this.render();
    }

    endDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      if (this.canvas.hasPointerCapture(event.pointerId)) {
        this.canvas.releasePointerCapture(event.pointerId);
      }
      this.drag = null;
      this.canvas.classList.remove('is-dragging');
    }

    handleKeyboardMove(event) {
      if (!this.image) return;

      const movements = {
        ArrowLeft: [15, 0],
        ArrowRight: [-15, 0],
        ArrowUp: [0, 15],
        ArrowDown: [0, -15]
      };

      if (!movements[event.key]) return;
      event.preventDefault();
      this.offsetX += movements[event.key][0];
      this.offsetY += movements[event.key][1];
      this.constrainOffsets();
      this.render();
    }

    constrainOffsets() {
      if (!this.image) return;

      const size = this.canvas.width;
      const displayWidth = this.image.naturalWidth * this.scale;
      const displayHeight = this.image.naturalHeight * this.scale;

      this.offsetX = Math.min(0, Math.max(size - displayWidth, this.offsetX));
      this.offsetY = Math.min(0, Math.max(size - displayHeight, this.offsetY));
    }

    render() {
      if (!this.image) return;

      const size = this.canvas.width;
      this.context.clearRect(0, 0, size, size);
      this.context.drawImage(
        this.image,
        this.offsetX,
        this.offsetY,
        this.image.naturalWidth * this.scale,
        this.image.naturalHeight * this.scale
      );
    }

    async applyCrop() {
      if (!this.image) return;

      this.applyButton.disabled = true;
      this.applyButton.textContent = 'Forbereder billedet…';

      try {
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = this.outputSize;
        outputCanvas.height = this.outputSize;

        const outputContext = outputCanvas.getContext('2d', { alpha: true });
        const factor = this.outputSize / this.canvas.width;

        outputContext.clearRect(0, 0, this.outputSize, this.outputSize);
        outputContext.save();
        outputContext.beginPath();
        outputContext.arc(
          this.outputSize / 2,
          this.outputSize / 2,
          this.outputSize / 2,
          0,
          Math.PI * 2
        );
        outputContext.clip();
        outputContext.drawImage(
          this.image,
          this.offsetX * factor,
          this.offsetY * factor,
          this.image.naturalWidth * this.scale * factor,
          this.image.naturalHeight * this.scale * factor
        );
        outputContext.restore();

        const blob = await new Promise((resolve, reject) => {
          outputCanvas.toBlob(
            (result) => (result ? resolve(result) : reject(new Error('Canvas export failed'))),
            'image/png'
          );
        });

        const baseName = (this.originalFile?.name || 'foto')
          .replace(/\.[^.]+$/, '')
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9-]+/gi, '-')
          .replace(/^-+|-+$/g, '')
          .toLowerCase() || 'foto';

        this.croppedFile = new File([blob], `${baseName}-valgt-udsnit.png`, {
          type: 'image/png',
          lastModified: Date.now()
        });

        this.revokeResultUrl();
        this.resultUrl = URL.createObjectURL(blob);
        this.resultImage.src = this.resultUrl;
        this.result.hidden = false;
        this.clearError();
        this.closeDialog();

        this.dispatchEvent(
          new CustomEvent('quiz-photo-crop-ready', {
            bubbles: true,
            detail: { fileName: this.croppedFile.name }
          })
        );
      } catch (error) {
        console.error('Quiz photo cropper could not export crop', error);
        this.showError('Billedet kunne ikke forberedes. Prøv et andet billede.');
      } finally {
        this.applyButton.disabled = false;
        this.applyButton.textContent = 'Brug dette udsnit';
      }
    }

    appendFilesToFormData(event) {
      if (!this.croppedFile) return;

      event.formData.set(
        'properties[Foto - valgt udsnit]',
        this.croppedFile,
        this.croppedFile.name
      );

      if (this.originalFile) {
        event.formData.set(
          'properties[_Originalt foto]',
          this.originalFile,
          this.originalFile.name
        );
      }
    }

    validateBeforeSubmit(event) {
      if (!this.sourceInput.files?.length) return;
      if (this.croppedFile) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.sourceInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.sourceInput.focus({ preventScroll: true });
    }

    openDialog() {
      if (!this.image) return;

      document.body.classList.add('qpc-dialog-open');
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        this.render();
        this.canvas.focus();
      });
    }

    closeDialog() {
      document.body.classList.remove('qpc-dialog-open');
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }

    cancelCrop() {
      if (this.croppedFile) {
        this.closeDialog();
        return;
      }
      this.closeDialog();
      this.originalFile = null;
      this.image = null;
      this.sourceInput.value = '';
      this.revokeImageUrl();
    }

    chooseAnotherPhoto() {
      this.closeDialog();
      this.clearCropResult();
      this.originalFile = null;
      this.image = null;
      this.sourceInput.value = '';
      this.revokeImageUrl();
      this.sourceInput.click();
    }

    rejectFile(message) {
      this.sourceInput.value = '';
      this.originalFile = null;
      this.image = null;
      this.clearCropResult();
      this.showError(message);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      this.error.textContent = '';
      this.error.hidden = true;
    }

    clearCropResult() {
      this.croppedFile = null;
      this.result.hidden = true;
      this.resultImage.removeAttribute('src');
      this.revokeResultUrl();
    }

    reset() {
      this.closeDialog();
      this.sourceInput.value = '';
      this.originalFile = null;
      this.image = null;
      this.clearCropResult();
      this.clearError();
      this.revokeImageUrl();
    }

    revokeImageUrl() {
      if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
    }

    revokeResultUrl() {
      if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
      this.resultUrl = null;
    }

    disconnectedCallback() {
      this.checkoutObserver?.disconnect();
      this.revokeImageUrl();
      this.revokeResultUrl();
    }
  }

  customElements.define('quiz-photo-cropper', QuizPhotoCropper);
})();
